// @src/layout/header/utils/getScopeByPathname.ts
export type Scope = 'client' | 'freelancer' | 'public' | 'unknown'

export type PathContext = {
  scope: Scope
  isMyPage: boolean // /my/* 경로인지 여부
}

export type ScopeContext = {
  /** 로그인 상태 */
  isLoggedIn: boolean
  /** 활성 프로필이 프리랜서인지 */
  hasActiveFreelancer?: boolean
  /** 활성 프로필이 기업인지 */
  hasActiveCompany?: boolean
  /** 프리랜서 프로필 존재 여부 (활성 여부 무관) */
  hasFreelancer?: boolean
  /** 기업 프로필 존재 여부 */
  hasCompany?: boolean
}

/**
 * pathname 기반 1차 스코프 결정
 */
const getInitialScopeByPathname = (pathname: string): Scope => {
  const p = pathname || ''

  // 마이페이지 영역은 확정 스코프
  if (p.startsWith('/my/client')) return 'client'
  if (p.startsWith('/my/freelancer')) return 'freelancer'

  // 클라이언트 영역
  if (p.startsWith('/client')) return 'client'

  // 프리랜서 영역
  if (p.startsWith('/freelancer')) return 'freelancer'

  // 루트 경로는 항상 프리랜서 스코프 (프리랜서 랜딩)
  if (p === '/') return 'freelancer'

  // 기타 공개 라우트 (인증/온보딩 제외)
  if (!p.startsWith('/auth') && !p.startsWith('/onboarding')) {
    return 'public'
  }

  return 'unknown'
}

/**
 * 스코프 결정 (개선안 A)
 * 
 * 1차: pathname prefix로 결정
 * 2차: 로그인 상태 + 활성 프로필/계정 타입으로 보정
 * 
 * @param pathname - 현재 경로
 * @param context - 로그인 상태 및 프로필 정보
 * @returns 최종 스코프
 */
export const getScopeByPathname = (
  pathname: string,
  context?: ScopeContext
): Scope => {
  const initialScope = getInitialScopeByPathname(pathname)

  // context가 없으면 1차 결정만 사용
  if (!context) {
    return initialScope
  }

  const { isLoggedIn, hasActiveFreelancer, hasActiveCompany, hasFreelancer, hasCompany } = context

  // 마이페이지 영역은 확정 스코프 (보정 불필요)
  if (pathname.startsWith('/my/client')) return 'client'
  if (pathname.startsWith('/my/freelancer')) return 'freelancer'

  // 루트 경로는 항상 프리랜서 스코프 (프리랜서 랜딩)
  if (pathname === '/') return 'freelancer'

  // 공개 영역 처리
  if (initialScope === 'public') {
    // 로그인하지 않았으면 공개 유지
    if (!isLoggedIn) return 'public'

    // 로그인했지만 활성 프로필이 없으면 공개 유지
    if (!hasActiveFreelancer && !hasActiveCompany) return 'public'

    // 활성 프로필이 있으면 해당 스코프로 보정
    if (hasActiveCompany) return 'client'
    if (hasActiveFreelancer) return 'freelancer'

    // 프로필 존재 여부로 보정 (활성 프로필이 없어도)
    if (hasCompany) return 'client'
    if (hasFreelancer) return 'freelancer'

    return 'public'
  }

  // /client, /freelancer 영역은 경로 우선
  // - 헤더/전환 버튼은 "현재 보고 있는 페이지 컨텍스트"를 기준으로 동작해야 함
  // - 예: /freelancer/* 에서는 항상 "고객으로 전환"이 보여야 함
  if (initialScope === 'client' || initialScope === 'freelancer') {
    return initialScope
  }

  // unknown은 그대로 반환
  return initialScope
}

/**
 * 경로의 스코프와 마이페이지 여부를 함께 반환
 */
export const getPathContext = (pathname: string, context?: ScopeContext): PathContext => {
  const p = pathname || ''
  const isMyPage = p.startsWith('/my/client') || p.startsWith('/my/freelancer')
  
  return {
    scope: getScopeByPathname(p, context),
    isMyPage,
  }
}
